/**
 * useGameClock — period management + dual-clock state for live mode.
 *
 * Extracted verbatim from EstadisticasNew.js:
 *  - The visible countdown (timeLeft / periodSeconds) driven by `timerRef`.
 *  - The "real-time camera clock" (realTimeRef + realTimeIntervalRef) which
 *    keeps ticking even when the game is paused, so action timestamps stay
 *    aligned with what actually happened on the floor.
 *  - startPeriod / pausePeriod / endPeriod handlers from renderLiveMode().
 *
 * Important quirk preserved: pausing the visible clock does NOT stop the
 * real-time ref. Only endPeriod resets it. This is what makes scout
 * timestamps match wall-clock time across timeouts and dead balls.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { STATS_MODE } from '../constants';

export function useGameClock({
  enabled = true,
  statsMode = STATS_MODE.LIVE,
  numberOfPeriods = 4,
  periodDurationMinutes = 10,
} = {}) {
  const [currentPeriod, setCurrentPeriod] = useState(1);
  const [periodSeconds, setPeriodSeconds] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [periodStarted, setPeriodStarted] = useState(false);

  const realTimeRef = useRef(0);
  const realTimeIntervalRef = useRef(null);
  const timerRef = useRef(null);

  // Visible countdown timer — only ticks in live mode while running
  useEffect(() => {
    if (!enabled) return undefined;
    if (isRunning && statsMode === STATS_MODE.LIVE) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            return 0;
          }
          return prev - 1;
        });
        setPeriodSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning, statsMode, enabled]);

  // Start (or resume) a period.
  // First start of a period: also kick off the real-time clock.
  const startPeriod = useCallback(() => {
    setIsRunning(true);

    setPeriodStarted((prevStarted) => {
      if (!prevStarted) {
        // First time we start this period — initialize the action clock
        if (!realTimeIntervalRef.current) {
          realTimeRef.current = 0;
          realTimeIntervalRef.current = setInterval(() => {
            realTimeRef.current += 1;
          }, 1000);
        }
        return true;
      }
      return prevStarted;
    });
  }, []);

  // Pause: stops the visible countdown but keeps the action clock ticking.
  const pausePeriod = useCallback(() => {
    setIsRunning(false);
  }, []);

  // End the period: stop the action clock, reset it, advance to next period.
  const endPeriod = useCallback(() => {
    setIsRunning(false);

    if (realTimeIntervalRef.current) {
      clearInterval(realTimeIntervalRef.current);
      realTimeIntervalRef.current = null;
    }

    if (currentPeriod < numberOfPeriods) {
      setCurrentPeriod((prev) => prev + 1);
      setTimeLeft(periodDurationMinutes * 60);
      setPeriodSeconds(0);
      setPeriodStarted(false);
      realTimeRef.current = 0;
    }
  }, [currentPeriod, numberOfPeriods, periodDurationMinutes]);

  // Configure for a freshly created game (handleConfigGame in original)
  const configureForNewGame = useCallback((durationMinutes) => {
    setTimeLeft((durationMinutes ?? periodDurationMinutes) * 60);
    setPeriodSeconds(0);
    setCurrentPeriod(1);
    setIsRunning(false);
    setPeriodStarted(false);
    realTimeRef.current = 0;

    if (realTimeIntervalRef.current) {
      clearInterval(realTimeIntervalRef.current);
      realTimeIntervalRef.current = null;
    }
  }, [periodDurationMinutes]);

  const reset = useCallback(() => {
    if (realTimeIntervalRef.current) {
      clearInterval(realTimeIntervalRef.current);
      realTimeIntervalRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCurrentPeriod(1);
    setPeriodSeconds(0);
    setTimeLeft(0);
    setIsRunning(false);
    setPeriodStarted(false);
    realTimeRef.current = 0;
  }, []);

  // Cleanup on unmount — same as the cleanup useEffect in original
  useEffect(() => {
    return () => {
      if (realTimeIntervalRef.current) {
        clearInterval(realTimeIntervalRef.current);
        realTimeIntervalRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Stable factory consumed by useStatsSession.recordAction in live mode.
  // Mirrors `realTimeRef.current` capture from recordAction.
  const computeActionTimestamp = useCallback(() => {
    return realTimeRef.current;
  }, []);

  return {
    // state
    currentPeriod,
    setCurrentPeriod,
    periodSeconds,
    setPeriodSeconds,
    timeLeft,
    setTimeLeft,
    isRunning,
    setIsRunning,
    periodStarted,
    setPeriodStarted,

    // refs (exposed because save logic needs to read realTimeRef.current)
    realTimeRef,

    // imperative controls
    startPeriod,
    pausePeriod,
    endPeriod,
    configureForNewGame,
    reset,

    // helper used by useStatsSession
    computeActionTimestamp,
  };
}
